import React, {useEffect, useRef, useState} from 'react';
import {Alert, Box, Button, DialogActions, DialogContent, IconButton, Paper, Portal, TextField, Tooltip, Typography} from '@mui/material';
import {Add, DeleteOutline, EditOutlined, StickyNote2Outlined} from '@mui/icons-material';
import {useForm} from 'react-hook-form';
import {noteServices} from '../../service/noteService';
import {connect} from 'react-redux';
import {alertActions} from '../../store/actions/alertActions';
import Spinner from '../spinner/Spinner';
import moment from 'moment';
import {CampaignDialogHeader, CampaignSection} from '../campaigns/CampaignDesign';
import {getSettingsColors, settingsInputStyles} from '../settings/SettingsDesign';

export function NotesForm({open, toggleDrawer123, campaignId, successMsg, errorMsg}) {
    const {handleSubmit, reset, register, watch, formState: {errors}} = useForm({defaultValues: {note: ''}});
    const [notes, setNotes] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editNote, setEditNote] = useState(null);
    const [error, setError] = useState('');
    const campaignRequest = useRef(0);
    const close = () => toggleDrawer123(false);
    const closeForm = () => {setShowForm(false); setEditNote(null); reset({note: ''});};
    const reportError = e => {
        const message = e?.response?.data?.error || 'Unable to update notes. Please try again.';
        setError(message); errorMsg(message);
    };

    useEffect(() => {
        const request = ++campaignRequest.current;
        setNotes([]); setError(''); setShowForm(false); setEditNote(null); reset({note: ''});
        setLoading(open);
        if (open) {
            noteServices.read({campaignId}).then(resp => {if (request === campaignRequest.current) setNotes(resp?.notes || []);})
                .catch(e => {if (request === campaignRequest.current) {const message = e?.response?.data?.error || 'Unable to load notes. Please try again.'; setError(message); errorMsg(message);}})
                .finally(() => {if (request === campaignRequest.current) setLoading(false);});
        }
        return () => {campaignRequest.current = request + 1;};
    }, [campaignId, open, errorMsg, reset]);

    const save = async formData => {
        const request = campaignRequest.current;
        setLoading(true); setError('');
        try {
            if (editNote) await noteServices.update({campaignId, noteId: editNote.id, formData});
            else await noteServices.create({campaignId, formData});
            if (request !== campaignRequest.current) return;
            successMsg(editNote ? 'Updated successfully.' : 'Added successfully.');
            closeForm();
            const response = await noteServices.read({campaignId});
            if (request === campaignRequest.current) setNotes(response?.notes || []);
        } catch (e) {if (request === campaignRequest.current) reportError(e);}
        finally {if (request === campaignRequest.current) setLoading(false);}
    };
    const remove = async noteId => {
        const request = campaignRequest.current;
        setLoading(true); setError('');
        try {
            await noteServices.archive({campaignId, noteId});
            if (request !== campaignRequest.current) return;
            setNotes(current => current.filter(item => item.id !== noteId));
            if (editNote?.id === noteId) closeForm();
            successMsg('Deleted successfully.');
        } catch (e) {if (request === campaignRequest.current) reportError(e);}
        finally {if (request === campaignRequest.current) setLoading(false);}
    };

    if (!open) return null;

    return <Portal><Paper component="section" role="region" aria-labelledby="campaign-notes-title" elevation={0}
        sx={{...settingsInputStyles, position: 'fixed', right: {xs: 8, sm: 16}, bottom: {xs: 8, sm: 16},
            zIndex: theme => theme.zIndex.drawer + 1, width: {xs: 'calc(100% - 16px)', sm: 420},
            maxHeight: {xs: 'min(70dvh, 600px)', sm: 'min(75dvh, 640px)'}, display: 'flex', flexDirection: 'column',
            overflow: 'hidden', borderRadius: '16px', border: '1px solid', borderColor: 'divider',
            bgcolor: theme => getSettingsColors(theme).card, backgroundImage: 'none', color: 'text.primary',
            '& .MuiDialogTitle-root': {m: 0, p: 2, fontSize: 20, fontWeight: 700, border: 0},
            '& .MuiDialogContent-root': {position: 'relative', px: 2, pt: 1, pb: 2, minHeight: 100, scrollbarGutter: 'stable'},
            '& .MuiDialogActions-root': {px: 2, py: 1.5, border: 0},
            '& .MuiTextField-root': {my: 0},
            '& .MuiOutlinedInput-input': {fontSize: {xs: 16, sm: 13}},
            '& .MuiButton-root': {textTransform: 'none', borderRadius: '8px', boxShadow: 'none', minHeight: 36}}}>
        <CampaignDialogHeader id="campaign-notes-title" title="Campaign notes" icon={StickyNote2Outlined}
            description="Select a campaign to view its notes." onClose={close}/>
        <DialogContent>
            {loading && <Spinner overComponentBox/>}
            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
            <Box sx={{display: 'grid', gap: 2}}>
                {showForm ? <CampaignSection title={editNote ? 'Edit note' : 'New note'}>
                    <Box component="form" onSubmit={handleSubmit(save)} sx={{display: 'grid', gap: 1.5}}>
                        <TextField autoFocus fullWidth multiline minRows={3} maxRows={6} label="Note" placeholder="Add a campaign update or reminder…"
                            {...register('note', {required: 'Enter a note.', minLength: {value: 3, message: 'Use at least 3 characters.'}, maxLength: {value: 255, message: 'Use no more than 255 characters.'}})}
                            error={!!errors.note} helperText={errors.note?.message || `${watch('note')?.length || 0}/255 characters`}
                            disabled={loading} slotProps={{inputLabel: {shrink: true}}}/>
                        <Box sx={{display: 'flex', gap: 1, justifyContent: 'flex-end'}}>
                            <Button color="inherit" onClick={closeForm} disabled={loading}>Cancel</Button>
                            <Button type="submit" variant="contained" disabled={loading}>Save note</Button>
                        </Box>
                    </Box>
                </CampaignSection> : <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                    <Typography sx={{fontSize: 13, color: 'text.secondary'}}>{notes.length} {notes.length === 1 ? 'note' : 'notes'}</Typography>
                    <Button variant="contained" startIcon={<Add/>} disabled={loading} onClick={() => {closeForm(); setShowForm(true);}}>Add note</Button>
                </Box>}
                {!notes.length && !loading ? <Box sx={{textAlign: 'center', py: 4, bgcolor: theme => getSettingsColors(theme).inset, borderRadius: '12px'}}>
                    <StickyNote2Outlined sx={{fontSize: 36, color: 'text.secondary', mb: 1}}/>
                    <Typography sx={{fontSize: 15, fontWeight: 600}}>No notes yet</Typography>
                    <Typography sx={{fontSize: 13, color: 'text.secondary', mt: 0.5}}>Add your first campaign update or reminder.</Typography>
                </Box> : notes.map(({id, note, createdAt}) => <Box key={id} sx={{p: 2, borderRadius: '12px', bgcolor: theme => getSettingsColors(theme).inset}}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1}}>
                        <Typography sx={{fontSize: 12, color: 'text.secondary'}}>{moment(createdAt).format('MMM D, YYYY · HH:mm')}</Typography>
                        <Box sx={{display: 'flex', gap: 0.5}}>
                            <Tooltip title="Edit note"><span><IconButton size="small" aria-label="Edit note" disabled={loading} onClick={() => {setEditNote({id, note}); reset({note}); setShowForm(true);}}><EditOutlined fontSize="small"/></IconButton></span></Tooltip>
                            <Tooltip title="Delete note"><span><IconButton size="small" aria-label="Delete note" disabled={loading} color="error" onClick={() => remove(id)}><DeleteOutline fontSize="small"/></IconButton></span></Tooltip>
                        </Box>
                    </Box>
                    <Typography sx={{fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{note}</Typography>
                </Box>)}
            </Box>
        </DialogContent>
        <DialogActions><Button color="inherit" onClick={close}>Close</Button></DialogActions>
    </Paper></Portal>;
}

export default connect(null, {successMsg: alertActions.success, errorMsg: alertActions.error, clear: alertActions.clear})(NotesForm);
